import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReportFilters, CompareMode } from '../../types/Report';
import { productService, Product } from '../../services/productService';
import { reportService } from '../../services/reportService';
import {
  FilterDimensionType,
  FilterOptionLite,
  appendReportFiltersToSearchParams,
  buildReportFiltersFromDimensionSelections,
  selectionsFromFilters,
  filterTypeToReportKey,
  listActiveDimensions,
  reportKeyToFilterType,
  itemsFromFiltersKey,
} from '../../utils/reportFilters';
import { ExecutiveDashboardView } from '../../utils/reportFiltersComparison';

interface FilterBarProps {
  filters: ReportFilters;
  onFiltersChange: (filters: ReportFilters) => void;
  onRun: () => void;
  onReset: () => void;
  loading?: boolean;
  /** When set (segmented drill-down page), segment is the locked primary filter; additional filters are secondary-only. */
  lockedSegment?: {
    urlType: string;
    id: string | number;
    name?: string;
  };
  /** Preserved on Run navigation for `/reports/executive-dashboard` URLs. */
  executiveDashboardView?: ExecutiveDashboardView;
}

type FilterType = FilterDimensionType;

type FilterOption = FilterOptionLite;

const URL_TYPE_TO_FILTER: Record<string, Exclude<FilterType, 'all'>> = {
  customer: 'customer',
  product: 'product',
  salesagent: 'sales agent',
  territory: 'territory',
  category: 'category',
  brand: 'brand',
};

function optionKey(o: FilterOption): string {
  return `${typeof o.id}:${String(o.id)}`;
}

function isOptionInList(list: FilterOption[], item: FilterOption): boolean {
  return list.some((x) => String(x.id) === String(item.id));
}

/** ReportFilters / URL only store ids; selections hydrate with name === id until resolved. */
function productNumericId(id: string | number): number | null {
  const n = typeof id === 'number' ? id : parseInt(String(id).trim(), 10);
  return Number.isFinite(n) && !Number.isNaN(n) ? n : null;
}

function isUnresolvedProductLabel(item: FilterOption): boolean {
  const n = productNumericId(item.id);
  if (n === null) return false;
  return item.name === String(item.id) || item.name === String(n);
}

/** URL/hydrated customer rows use the id string as the label until resolved. */
function isUnresolvedCustomerLabel(item: FilterOption): boolean {
  return String(item.name) === String(item.id);
}

function filterSearchFieldPaddingRight(selected_count: number, loading: boolean): string {
  if (selected_count > 0 && loading) return '5.5rem';
  if (selected_count > 0) return '4.5rem';
  if (loading) return '3.5rem';
  return '2.75rem';
}

function SearchDropdownChevron({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{
        transform: open ? 'rotate(180deg)' : 'none',
        transition: 'transform 0.15s ease',
      }}
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFiltersChange,
  onRun,
  onReset,
  loading = false,
  lockedSegment,
  executiveDashboardView,
}) => {
  const navigate = useNavigate();
  const [localFilters, setLocalFilters] = useState<ReportFilters>(filters);

  const lockedPrimaryType: FilterType | null = lockedSegment
    ? URL_TYPE_TO_FILTER[lockedSegment.urlType.toLowerCase()] || null
    : null;

  const lockedPrimarySelected: FilterOption[] = useMemo(() => {
    if (!lockedSegment || !lockedPrimaryType) return [];
    const id =
      lockedPrimaryType === 'product' && typeof lockedSegment.id === 'string'
        ? lockedSegment.id.trim()
        : lockedSegment.id;
    return [
      {
        id,
        name: lockedSegment.name || String(id),
      },
    ];
  }, [lockedSegment, lockedPrimaryType]);

  const [primaryType, setPrimaryType] = useState<FilterType>('all');
  const [secondaryType, setSecondaryType] = useState<FilterType>('all');
  const [primarySelected, setPrimarySelected] = useState<FilterOption[]>([]);
  const [secondarySelected, setSecondarySelected] = useState<FilterOption[]>([]);

  const [searchPrimary, setSearchPrimary] = useState('');
  const [searchSecondary, setSearchSecondary] = useState('');
  const [debouncedPrimary, setDebouncedPrimary] = useState('');
  const [debouncedSecondary, setDebouncedSecondary] = useState('');

  const [resultsPrimary, setResultsPrimary] = useState<FilterOption[]>([]);
  const [resultsSecondary, setResultsSecondary] = useState<FilterOption[]>([]);
  const [loadingPrimary, setLoadingPrimary] = useState(false);
  const [loadingSecondary, setLoadingSecondary] = useState(false);
  const [showDropPrimary, setShowDropPrimary] = useState(false);
  const [showDropSecondary, setShowDropSecondary] = useState(false);

  const refPrimary = useRef<HTMLDivElement>(null);
  const refSecondary = useRef<HTMLDivElement>(null);
  const primary_selected_ref = useRef(primarySelected);
  const secondary_selected_ref = useRef(secondarySelected);
  primary_selected_ref.current = primarySelected;
  secondary_selected_ref.current = secondarySelected;

  const filtersSerialized = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filtersSerialized]);

  // Hydrate from URL/parent when filters change (e.g. navigation)
  useEffect(() => {
    if (lockedSegment && lockedPrimaryType) {
      setPrimaryType(lockedPrimaryType);
      setPrimarySelected(lockedPrimarySelected);
      const pk = filterTypeToReportKey(lockedPrimaryType);
      const dims = listActiveDimensions(filters).filter((k) => k !== pk);
      if (dims.length > 0) {
        const sk = dims[0];
        const t = reportKeyToFilterType(sk);
        setSecondaryType(t ?? 'all');
        setSecondarySelected(itemsFromFiltersKey(filters, sk));
      } else {
        setSecondaryType('all');
        setSecondarySelected([]);
      }
      return;
    }

    const {
      primaryType: pt,
      secondaryType: st,
      primaryItems,
      secondaryItems,
    } = selectionsFromFilters(filters);
    setPrimaryType(pt);
    setSecondaryType(st);
    setPrimarySelected(primaryItems);
    setSecondarySelected(secondaryItems);
  }, [filtersSerialized, lockedSegment, lockedPrimaryType, lockedPrimarySelected]);

  const primary_product_ids_key = useMemo(() => {
    if (primaryType !== 'product') return '';
    return [...primarySelected.map((x) => String(x.id))].sort().join(',');
  }, [primaryType, primarySelected]);

  const secondary_product_ids_key = useMemo(() => {
    if (secondaryType !== 'product') return '';
    return [...secondarySelected.map((x) => String(x.id))].sort().join(',');
  }, [secondaryType, secondarySelected]);

  const primary_customer_ids_key = useMemo(() => {
    if (primaryType !== 'customer') return '';
    return [...primarySelected.map((x) => String(x.id))].sort().join(',');
  }, [primaryType, primarySelected]);

  const secondary_customer_ids_key = useMemo(() => {
    if (secondaryType !== 'customer') return '';
    return [...secondarySelected.map((x) => String(x.id))].sort().join(',');
  }, [secondaryType, secondarySelected]);

  useEffect(() => {
    let cancelled = false;

    const enrich_product_row = async (item: FilterOption): Promise<FilterOption | null> => {
      const num_id = productNumericId(item.id);
      if (num_id === null) return null;
      try {
        const p: Product = await productService.getProductById(num_id);
        return { id: item.id, name: p.name, code: p.product_code };
      } catch {
        return null;
      }
    };

    const enrich_customer_row = async (item: FilterOption): Promise<FilterOption | null> => {
      const id_str = String(item.id).trim();
      if (!id_str) return null;
      try {
        const rows = await reportService.searchCustomers(id_str);
        const hit = rows.find((r) => String(r.id) === String(item.id));
        return hit ? { id: item.id, name: hit.name } : null;
      } catch {
        return null;
      }
    };

    const merge_label_updates = (
      pairs: Array<readonly [string, FilterOption] | null>,
      set_items: React.Dispatch<React.SetStateAction<FilterOption[]>>
    ) => {
      const updates = new Map<string, FilterOption>();
      for (const row of pairs) {
        if (row) updates.set(row[0], row[1]);
      }
      if (updates.size === 0) return;
      set_items((prev) => prev.map((row) => updates.get(String(row.id)) ?? row));
    };

    const enrich_product_labels = async (
      items: FilterOption[],
      set_items: React.Dispatch<React.SetStateAction<FilterOption[]>>
    ) => {
      const targets = items.filter(isUnresolvedProductLabel);
      if (targets.length === 0) return;
      const pairs = await Promise.all(
        targets.map(async (item) => {
          const next = await enrich_product_row(item);
          return next ? ([String(item.id), next] as const) : null;
        })
      );
      if (cancelled) return;
      merge_label_updates(pairs, set_items);
    };

    const enrich_customer_labels = async (
      items: FilterOption[],
      set_items: React.Dispatch<React.SetStateAction<FilterOption[]>>
    ) => {
      const targets = items.filter(isUnresolvedCustomerLabel);
      if (targets.length === 0) return;
      const pairs = await Promise.all(
        targets.map(async (item) => {
          const next = await enrich_customer_row(item);
          return next ? ([String(item.id), next] as const) : null;
        })
      );
      if (cancelled) return;
      merge_label_updates(pairs, set_items);
    };

    if (!lockedSegment && primaryType === 'product' && primary_product_ids_key) {
      void enrich_product_labels(primary_selected_ref.current, setPrimarySelected);
    }
    if (secondaryType === 'product' && secondary_product_ids_key) {
      void enrich_product_labels(secondary_selected_ref.current, setSecondarySelected);
    }
    if (!lockedSegment && primaryType === 'customer' && primary_customer_ids_key) {
      void enrich_customer_labels(primary_selected_ref.current, setPrimarySelected);
    }
    if (secondaryType === 'customer' && secondary_customer_ids_key) {
      void enrich_customer_labels(secondary_selected_ref.current, setSecondarySelected);
    }

    return () => {
      cancelled = true;
    };
  }, [
    lockedSegment,
    primaryType,
    secondaryType,
    primary_product_ids_key,
    secondary_product_ids_key,
    primary_customer_ids_key,
    secondary_customer_ids_key,
  ]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedPrimary(searchPrimary), 300);
    return () => clearTimeout(t);
  }, [searchPrimary]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSecondary(searchSecondary), 300);
    return () => clearTimeout(t);
  }, [searchSecondary]);

  const primaryTypeForSearch = lockedSegment ? lockedPrimaryType! : primaryType;
  const effectiveSecondaryEnabled =
    !!lockedSegment || (primaryType !== 'all' && primarySelected.length > 0);

  /** Filter 2 cannot use the same dimension as filter 1 (or locked segment). */
  const secondary_blocked_dimension: FilterType | null =
    lockedSegment && lockedPrimaryType
      ? lockedPrimaryType
      : primaryType !== 'all'
        ? primaryType
        : null;

  const runSearch = useCallback(
    async (
      filterType: Exclude<FilterType, 'all'>,
      debounced: string,
      setResults: (r: FilterOption[]) => void,
      setLoading: (v: boolean) => void
    ) => {
      if (!debounced.trim()) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        let results: FilterOption[] = [];
        switch (filterType) {
          case 'product': {
            const productResponse = await productService.searchProductsPaginated(
              debounced,
              1,
              50,
              false,
              true
            );
            results = productResponse.products.map((p: Product) => ({
              id: p.id,
              name: p.name,
              code: p.product_code,
            }));
            break;
          }
          case 'brand': {
            const brandStrings = await reportService.searchBrandsFromProducts(debounced);
            results = brandStrings.map((brand: string) => ({ id: brand, name: brand }));
            break;
          }
          case 'category': {
            const categories = await reportService.searchCategories(debounced);
            results = categories.map((cat: string) => ({ id: cat, name: cat }));
            break;
          }
          case 'customer': {
            const customers = await reportService.searchCustomers(debounced);
            results = customers.map((cust: { id: string; name: string }) => ({
              id: cust.id,
              name: cust.name,
            }));
            break;
          }
          case 'sales agent': {
            const agents = await reportService.searchSalesAgents(debounced);
            results = agents.map((agent: string) => ({ id: agent, name: agent }));
            break;
          }
          case 'territory': {
            const territories = await reportService.searchTerritories(debounced);
            results = territories.map((terr: string) => ({ id: terr, name: terr }));
            break;
          }
        }
        setResults(results);
      } catch (e) {
        console.error('Filter search error:', e);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (primaryTypeForSearch === 'all') {
      setResultsPrimary([]);
      return;
    }
    runSearch(primaryTypeForSearch, debouncedPrimary, setResultsPrimary, setLoadingPrimary);
  }, [debouncedPrimary, primaryTypeForSearch, runSearch]);

  useEffect(() => {
    if (!effectiveSecondaryEnabled || secondaryType === 'all') {
      setResultsSecondary([]);
      return;
    }
    runSearch(
      secondaryType as Exclude<FilterType, 'all'>,
      debouncedSecondary,
      setResultsSecondary,
      setLoadingSecondary
    );
  }, [debouncedSecondary, secondaryType, effectiveSecondaryEnabled, runSearch]);

  useEffect(() => {
    // Capture phase so outside-close still runs if a child stops propagation on bubble.
    const close = (e: PointerEvent) => {
      const t = e.target;
      if (!t || !(t instanceof Node)) return;
      if (refPrimary.current?.contains(t)) return;
      if (refSecondary.current?.contains(t)) return;
      setShowDropPrimary(false);
      setShowDropSecondary(false);
    };
    document.addEventListener('pointerdown', close, true);
    return () => document.removeEventListener('pointerdown', close, true);
  }, []);

  const mergeToLocalFilters = useCallback(
    (
      nextPrimaryType: FilterType,
      nextSecondaryType: FilterType,
      nextPrimary: FilterOption[],
      nextSecondary: FilterOption[]
    ) => {
      setLocalFilters((prev) =>
        buildReportFiltersFromDimensionSelections(
          prev,
          lockedSegment && lockedPrimaryType ? lockedPrimaryType : nextPrimaryType,
          nextSecondaryType,
          lockedSegment ? lockedPrimarySelected : nextPrimary,
          nextSecondary
        )
      );
    },
    [lockedSegment, lockedPrimaryType, lockedPrimarySelected]
  );

  const handleDateChange = useCallback((field: 'startDate' | 'endDate', value: string) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCompareModeChange = useCallback((mode: CompareMode) => {
    setLocalFilters((prev) => ({ ...prev, compareMode: mode }));
  }, []);

  const handlePrimaryTypeChange = useCallback(
    (type: FilterType) => {
      setPrimaryType(type);
      setSearchPrimary('');
      setPrimarySelected([]);
      setSecondaryType('all');
      setSecondarySelected([]);
      setSearchSecondary('');
      if (type === 'all') {
        setLocalFilters((prev) => {
          const u = { ...prev };
          delete u.productIds;
          delete u.categoryIds;
          delete u.brandIds;
          delete u.customerIds;
          delete u.territoryIds;
          delete u.salesPersonIds;
          delete u.primary_dimension;
          delete u.secondary_dimension;
          return u;
        });
      } else {
        mergeToLocalFilters(type, 'all', [], []);
      }
    },
    [mergeToLocalFilters]
  );

  const handleSecondaryTypeChange = useCallback(
    (type: FilterType) => {
      setSecondaryType(type);
      setSearchSecondary('');
      setSecondarySelected([]);
      mergeToLocalFilters(primaryType, type, primarySelected, []);
    },
    [mergeToLocalFilters, primaryType, primarySelected]
  );

  const addPrimary = useCallback(
    (item: FilterOption) => {
      if (isOptionInList(primarySelected, item)) return;
      const next = [...primarySelected, item];
      setPrimarySelected(next);
      mergeToLocalFilters(primaryType, secondaryType, next, secondarySelected);
    },
    [primarySelected, primaryType, secondaryType, secondarySelected, mergeToLocalFilters]
  );

  const removePrimary = useCallback(
    (item: FilterOption) => {
      const next = primarySelected.filter((x) => String(x.id) !== String(item.id));
      setPrimarySelected(next);
      mergeToLocalFilters(primaryType, secondaryType, next, secondarySelected);
    },
    [primarySelected, primaryType, secondaryType, secondarySelected, mergeToLocalFilters]
  );

  const addSecondary = useCallback(
    (item: FilterOption) => {
      if (isOptionInList(secondarySelected, item)) return;
      if (
        primaryType === secondaryType &&
        primarySelected.some((p) => String(p.id) === String(item.id))
      ) {
        return;
      }
      const next = [...secondarySelected, item];
      setSecondarySelected(next);
      mergeToLocalFilters(primaryType, secondaryType, primarySelected, next);
      setSearchSecondary('');
    },
    [secondarySelected, primaryType, secondaryType, primarySelected, mergeToLocalFilters]
  );

  const removeSecondary = useCallback(
    (item: FilterOption) => {
      const next = secondarySelected.filter((x) => String(x.id) !== String(item.id));
      setSecondarySelected(next);
      mergeToLocalFilters(primaryType, secondaryType, primarySelected, next);
    },
    [secondarySelected, primaryType, secondaryType, primarySelected, mergeToLocalFilters]
  );

  const handleRun = useCallback(() => {
    const built = buildReportFiltersFromDimensionSelections(
      localFilters,
      lockedSegment ? lockedPrimaryType! : primaryType,
      secondaryType,
      lockedSegment ? lockedPrimarySelected : primarySelected,
      secondarySelected
    );
    onFiltersChange(built);
    onRun();

    const params = appendReportFiltersToSearchParams(built);
    if (executiveDashboardView && !lockedSegment) {
      params.set('dashboardView', executiveDashboardView);
    }

    if (lockedSegment) {
      const id =
        lockedPrimaryType === 'product' && typeof lockedSegment.id === 'string'
          ? lockedSegment.id.trim()
          : lockedSegment.id;
      const encoded = encodeURIComponent(String(id));
      navigate(
        `/reports/executive-dashboard/${lockedSegment.urlType}/${encoded}?${params.toString()}`
      );
      return;
    }

    navigate(`/reports/executive-dashboard?${params.toString()}`);
  }, [
    localFilters,
    lockedSegment,
    lockedPrimaryType,
    lockedPrimarySelected,
    primaryType,
    secondaryType,
    primarySelected,
    secondarySelected,
    onFiltersChange,
    onRun,
    navigate,
    executiveDashboardView,
  ]);

  const handleReset = useCallback(() => {
    const defaultFilters: ReportFilters = { compareMode: 'yoy' };
    const now = new Date();
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDate = new Date(now.getFullYear(), 0, 1);
    defaultFilters.startDate = startDate.toISOString().split('T')[0];
    defaultFilters.endDate = endDate.toISOString().split('T')[0];

    if (lockedSegment && lockedPrimaryType) {
      setLocalFilters({
        ...defaultFilters,
        ...segmentToFilterPatch(lockedPrimaryType, lockedPrimarySelected),
      });
      setPrimaryType(lockedPrimaryType);
      setPrimarySelected(lockedPrimarySelected);
      setSecondaryType('all');
      setSecondarySelected([]);
    } else {
      setLocalFilters(defaultFilters);
      setPrimaryType('all');
      setSecondaryType('all');
      setPrimarySelected([]);
      setSecondarySelected([]);
    }
    setSearchPrimary('');
    setSearchSecondary('');
    onFiltersChange(
      lockedSegment && lockedPrimaryType
        ? {
            ...defaultFilters,
            ...segmentToFilterPatch(lockedPrimaryType, lockedPrimarySelected),
          }
        : defaultFilters
    );
    onReset();
  }, [lockedSegment, lockedPrimaryType, lockedPrimarySelected, onFiltersChange, onReset]);

  const excludePrimaryIdsForSecondary = useMemo(() => {
    const set = new Set<string>();
    if (primaryType === secondaryType) {
      primarySelected.forEach((p) => set.add(String(p.id)));
    }
    if (
      lockedSegment &&
      lockedPrimaryType &&
      secondaryType === lockedPrimaryType &&
      lockedPrimarySelected.length
    ) {
      lockedPrimarySelected.forEach((p) => set.add(String(p.id)));
    }
    return set;
  }, [
    primaryType,
    secondaryType,
    primarySelected,
    lockedSegment,
    lockedPrimaryType,
    lockedPrimarySelected,
  ]);

  const dropdownPanel = (
    filterType: Exclude<FilterType, 'all'>,
    selected: FilterOption[],
    results: FilterOption[],
    loading: boolean,
    onRemoveSelected: (item: FilterOption) => void,
    onAdd: (item: FilterOption) => void,
    excludeIds: Set<string>
  ) => {
    const selectedKeys = new Set(selected.map((s) => String(s.id)));
    const addRows = results.filter((r) => {
      const k = String(r.id);
      if (selectedKeys.has(k)) return false;
      if (excludeIds.has(k)) return false;
      return true;
    });

    return (
      <div
        style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '0.25rem',
          backgroundColor: 'white',
          border: '1px solid #ced4da',
          borderRadius: '4px',
          maxHeight: '260px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        {selected.length > 0 && (
          <>
            <div
              style={{
                padding: '0.35rem 0.5rem',
                borderBottom: '1px solid #e9ecef',
                background: '#f8f9fa',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#495057',
              }}
            >
              Selected (click to remove)
            </div>
            {selected.map((item) => (
              <div
                key={`s-${optionKey(item)}`}
                role="button"
                tabIndex={0}
                onClick={() => onRemoveSelected(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onRemoveSelected(item);
                }}
                style={{ padding: '0.5rem', cursor: 'pointer', background: '#fff' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                }}
              >
                {item.code ? (
                  <div>
                    <div style={{ fontWeight: 500 }}>{item.name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>{item.code}</div>
                  </div>
                ) : (
                  <div style={{ fontWeight: 500 }}>{item.name}</div>
                )}
              </div>
            ))}
          </>
        )}
        {(selected.length > 0 || addRows.length > 0) && (
          <div
            style={{
              padding: '0.35rem 0.5rem',
              borderTop: selected.length > 0 ? '1px solid #e9ecef' : undefined,
              borderBottom: addRows.length > 0 ? '1px solid #e9ecef' : undefined,
              background: '#f8f9fa',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#495057',
            }}
          >
            Add more{loading ? ' …' : ''}
          </div>
        )}
        {addRows.map((item) => (
          <div
            key={`a-${optionKey(item)}`}
            role="button"
            tabIndex={0}
            onClick={() => onAdd(item)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onAdd(item);
            }}
            style={{ padding: '0.5rem', cursor: 'pointer' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            {item.code ? (
              <div>
                <div style={{ fontWeight: 500 }}>{item.name}</div>
                <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>{item.code}</div>
              </div>
            ) : (
              <div style={{ fontWeight: 500 }}>{item.name}</div>
            )}
          </div>
        ))}
        {!loading && selected.length === 0 && addRows.length === 0 && (
          <div style={{ padding: '0.75rem', color: '#6c757d', fontSize: '0.9rem' }}>
            Type to search for {filterType}…
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        style={{
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap',
          alignItems: 'end',
        }}
      >
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Start Date
          </label>
          <input
            type="date"
            value={localFilters.startDate || ''}
            onChange={(e) => handleDateChange('startDate', e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '1rem',
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            End Date
          </label>
          <input
            type="date"
            value={localFilters.endDate || ''}
            onChange={(e) => handleDateChange('endDate', e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '1rem',
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Compare Mode
          </label>
          <select
            value={localFilters.compareMode || 'yoy'}
            onChange={(e) => handleCompareModeChange(e.target.value as CompareMode)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '1rem',
              minWidth: '150px',
            }}
          >
            <option value="yoy">Year-over-Year</option>
            <option value="previous">Previous Period</option>
          </select>
        </div>

        {!lockedSegment && (
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Filter 1
            </label>
            <select
              value={primaryType}
              onChange={(e) => handlePrimaryTypeChange(e.target.value as FilterType)}
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '1rem',
                minWidth: '150px',
              }}
            >
              <option value="all">All</option>
              <option value="category">Category</option>
              <option value="brand">Brand</option>
              <option value="product">Product</option>
              <option value="customer">Customer</option>
              <option value="sales agent">Sales Agent</option>
              <option value="territory">Territory</option>
            </select>
          </div>
        )}

        {lockedSegment && lockedPrimaryType && (
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Segment
            </label>
            <div style={{ fontSize: '0.95rem', padding: '0.35rem 0' }}>
              {lockedPrimarySelected[0]?.name || lockedSegment.id}
            </div>
          </div>
        )}

        {!lockedSegment && primaryType !== 'all' && (
          <div style={{ position: 'relative' }} ref={refPrimary}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Search {primaryType.charAt(0).toUpperCase() + primaryType.slice(1)}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={searchPrimary}
                onChange={(e) => {
                  setSearchPrimary(e.target.value);
                  setShowDropPrimary(true);
                }}
                onFocus={() => setShowDropPrimary(true)}
                placeholder={`Search ${primaryType}…`}
                style={{
                  padding: '0.5rem',
                  paddingRight: filterSearchFieldPaddingRight(
                    primarySelected.length,
                    loadingPrimary
                  ),
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  minWidth: '250px',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  right: '0.2rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                }}
              >
                {loadingPrimary && (
                  <svg
                    style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }}
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <circle
                      style={{ opacity: 0.25 }}
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      style={{ opacity: 0.75 }}
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {primarySelected.length > 0 && (
                  <span
                    title={`${primarySelected.length} selected`}
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: '#495057',
                      lineHeight: 1,
                      userSelect: 'none',
                    }}
                  >
                    {primarySelected.length}
                  </span>
                )}
                <button
                  type="button"
                  aria-expanded={showDropPrimary}
                  aria-label={showDropPrimary ? 'Close filter list' : 'Open filter list'}
                  onClick={() => setShowDropPrimary((v) => !v)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.2rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#6c757d',
                  }}
                >
                  <SearchDropdownChevron open={showDropPrimary} />
                </button>
              </div>
              {showDropPrimary &&
                dropdownPanel(
                  primaryType,
                  primarySelected,
                  resultsPrimary,
                  loadingPrimary,
                  removePrimary,
                  addPrimary,
                  new Set<string>()
                )}
            </div>
          </div>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Filter 2
          </label>
          <select
            value={secondaryType}
            onChange={(e) => handleSecondaryTypeChange(e.target.value as FilterType)}
            disabled={!effectiveSecondaryEnabled}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '1rem',
              minWidth: '150px',
              opacity: effectiveSecondaryEnabled ? 1 : 0.5,
            }}
          >
            <option value="all">All</option>
            {(
              [
                ['category', 'Category'],
                ['brand', 'Brand'],
                ['product', 'Product'],
                ['customer', 'Customer'],
                ['sales agent', 'Sales Agent'],
                ['territory', 'Territory'],
              ] as const
            ).map(([value, label]) => (
              <option
                key={value}
                value={value}
                disabled={
                  secondary_blocked_dimension !== null && value === secondary_blocked_dimension
                }
              >
                {label}
              </option>
            ))}
          </select>
        </div>

        {effectiveSecondaryEnabled && secondaryType !== 'all' && (
          <div style={{ position: 'relative' }} ref={refSecondary}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Search {secondaryType.charAt(0).toUpperCase() + secondaryType.slice(1)}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={searchSecondary}
                onChange={(e) => {
                  setSearchSecondary(e.target.value);
                  setShowDropSecondary(true);
                }}
                onFocus={() => setShowDropSecondary(true)}
                placeholder={`Search ${secondaryType}…`}
                style={{
                  padding: '0.5rem',
                  paddingRight: filterSearchFieldPaddingRight(
                    secondarySelected.length,
                    loadingSecondary
                  ),
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  minWidth: '250px',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  right: '0.2rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6c757d',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                }}
              >
                {loadingSecondary && (
                  <svg
                    style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }}
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <circle
                      style={{ opacity: 0.25 }}
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      style={{ opacity: 0.75 }}
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {secondarySelected.length > 0 && (
                  <span
                    title={`${secondarySelected.length} selected`}
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: '#495057',
                      lineHeight: 1,
                      userSelect: 'none',
                    }}
                  >
                    {secondarySelected.length}
                  </span>
                )}
                <button
                  type="button"
                  aria-expanded={showDropSecondary}
                  aria-label={showDropSecondary ? 'Close filter list' : 'Open filter list'}
                  onClick={() => setShowDropSecondary((v) => !v)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.2rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#6c757d',
                  }}
                >
                  <SearchDropdownChevron open={showDropSecondary} />
                </button>
              </div>
              {showDropSecondary &&
                dropdownPanel(
                  secondaryType,
                  secondarySelected,
                  resultsSecondary,
                  loadingSecondary,
                  removeSecondary,
                  addSecondary,
                  excludePrimaryIdsForSecondary
                )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={handleRun}
            disabled={loading}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: loading ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {loading ? (
              <>
                <svg
                  style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }}
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    style={{ opacity: 0.25 }}
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    style={{ opacity: 0.75 }}
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Running…</span>
              </>
            ) : (
              <span>Run Report</span>
            )}
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
            }}
          >
            Reset
          </button>
        </div>
      </div>
    </>
  );
};

function segmentToFilterPatch(
  t: Exclude<FilterType, 'all'>,
  items: FilterOption[]
): Partial<ReportFilters> {
  const ids = items.map((i) => i.id);
  switch (t) {
    case 'product':
      return {
        productIds: ids
          .map((id) => (typeof id === 'number' ? id : parseInt(String(id), 10)))
          .filter((n) => !isNaN(n)),
      };
    case 'category':
      return { categoryIds: ids.map(String) };
    case 'brand':
      return { brandIds: ids.map(String) };
    case 'customer':
      return { customerIds: ids.map(String) };
    case 'territory':
      return { territoryIds: ids.map(String) };
    case 'sales agent':
      return { salesPersonIds: ids.map(String) };
    default:
      return {};
  }
}

export default React.memo(FilterBar);
